import { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import { clearToken, isDesktopClient, getRefreshToken, setRefreshToken, silentRefresh } from 'utils/axios';
import { redirectToLogin } from 'utils/authRedirect';
import { openSnackbar } from 'api/snackbar';

interface SessionTimerProps {
  expiresIn: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  variant?: 'default' | 'compact';
}

// Converte {h,m,s} em total de segundos
const toTotalSeconds = (e: { hours: number; minutes: number; seconds: number }) => e.hours * 3600 + e.minutes * 60 + e.seconds;

// Converte total de segundos em {h,m,s}
const fromTotalSeconds = (total: number) => {
  const s = Math.max(0, total);
  return { hours: Math.floor(s / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
};

export default function SessionTimer({ expiresIn, variant = 'default' }: SessionTimerProps) {
  const theme = useTheme();

  // Duração base recebida da API (denominador da barra de progresso)
  const totalSeconds = toTotalSeconds(expiresIn);

  // Deadline ABSOLUTO (relógio real). Recalculado apenas quando expiresIn muda.
  // Contar a partir do relógio real evita que o timer "trave"/atrase quando a aba
  // fica em segundo plano (o navegador estrangula o setInterval nesse caso).
  const deadlineRef = useRef<number>(Date.now() + totalSeconds * 1000);
  const [remaining, setRemaining] = useState<number>(totalSeconds);

  // Refs para controlar se os alertas já foram mostrados
  const warningShown = useRef(false);
  const criticalShown = useRef(false);
  // Evita disparar várias renovações simultâneas no Electron
  const renewingRef = useRef(false);

  // Quando o tempo é renovado (login / refresh / /auth/me), redefine o deadline e os alertas
  useEffect(() => {
    deadlineRef.current = Date.now() + totalSeconds * 1000;
    setRemaining(totalSeconds);

    if (totalSeconds > 600) {
      warningShown.current = false;
    }
    if (totalSeconds > 120) {
      criticalShown.current = false;
    }
  }, [totalSeconds]);

  useEffect(() => {
    // Calcula o restante sempre a partir do relógio real, nunca por contagem de ticks
    const tick = () => {
      const msLeft = deadlineRef.current - Date.now();

      if (msLeft <= 0) {
        setRemaining(0);
        // No app desktop (Electron): renova silenciosamente em vez de deslogar,
        // mantendo a sessão viva enquanto o refresh token for válido.
        if (isDesktopClient() && getRefreshToken() && !renewingRef.current) {
          renewingRef.current = true;
          silentRefresh()
            .then((ok) => {
              if (ok) {
                deadlineRef.current = Date.now() + totalSeconds * 1000;
                setRemaining(totalSeconds);
                warningShown.current = false;
                criticalShown.current = false;
              } else {
                clearToken();
                setRefreshToken(null);
                redirectToLogin();
              }
            })
            .finally(() => {
              renewingRef.current = false;
            });
          return;
        }
        // Web (ou sem refresh): expira e redireciona
        clearToken();
        redirectToLogin();
        return;
      }

      const remainingSeconds = Math.ceil(msLeft / 1000);
      setRemaining(remainingSeconds);

      // Verifica se restam 10 minutos (600 segundos) e mostra alerta
      if (remainingSeconds <= 600 && !warningShown.current) {
        warningShown.current = true;
        openSnackbar({
          open: true,
          message: '⚠️ Atenção! Sua sessão expira em 10 minutos. Salve suas alterações para não perdê-las.',
          variant: 'alert',
          alert: {
            color: 'warning',
            variant: 'filled'
          },
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        } as any);
      }

      // Verifica se restam 2 minutos (120 segundos) e mostra alerta crítico
      if (remainingSeconds <= 120 && !criticalShown.current) {
        criticalShown.current = true;
        openSnackbar({
          open: true,
          message: '🚨 URGENTE! Sua sessão expira em 2 minutos. Salve suas alterações imediatamente!',
          variant: 'alert',
          alert: {
            color: 'error',
            variant: 'filled'
          },
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        } as any);
      }
    };

    tick(); // sincroniza imediatamente ao montar
    const timer = setInterval(tick, 1000);

    // Resincroniza na hora ao voltar para a aba ou reganhar foco
    // (cobre o estrangulamento de timers em abas em segundo plano)
    const resync = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, []);

  // Tempo restante para exibição
  const timeLeft = fromTotalSeconds(remaining);

  // Calcula a porcentagem de tempo restante
  const progressPercentage = Math.max(0, Math.min(100, (remaining / totalSeconds) * 100));

  // Determina a cor baseada no tempo restante
  const getColor = () => {
    const totalSeconds = timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;

    // Se restam menos de 2 minutos, vermelho crítico
    if (totalSeconds <= 120) return theme.palette.error.main;
    // Se restam menos de 10 minutos, laranja de aviso
    if (totalSeconds <= 600) return theme.palette.warning.main;
    // Caso contrário, verde
    return theme.palette.success.main;
  };

  // Formata o tempo para exibição
  const formatTime = () => {
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    }
    if (timeLeft.minutes > 0) {
      return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
    }
    return `${timeLeft.seconds}s`;
  };

  // Verifica se está no estado crítico (menos de 2 minutos)
  const isCritical = timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds <= 120;

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <ClockCircleOutlined
          style={{
            color: getColor(),
            fontSize: 14,
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: isCritical ? getColor() : 'text.secondary',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        >
          {formatTime()}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ClockCircleOutlined
          style={{
            color: getColor(),
            fontSize: 16,
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            color: isCritical ? getColor() : 'text.secondary',
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        >
          Sessão expira em: {formatTime()}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        sx={{
          height: 3,
          borderRadius: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getColor(),
            borderRadius: 1.5,
            animation: isCritical ? 'pulse 1s infinite' : 'none'
          },
          '@keyframes blink': {
            '0%, 50%': { opacity: 1 },
            '51%, 100%': { opacity: 0.5 }
          },
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.7 },
            '100%': { opacity: 1 }
          }
        }}
      />
    </Box>
  );
}
